import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LABOUR_CLOSE_OUTCOME_LABELS,
  LABOUR_CLOSE_OUTCOMES,
  type LabourCloseOutcome,
} from '@wodi/domain';
import type { LabourRecordView } from '@wodi/shared';
import { Button, Dialog, DialogActions, Notice, RadioGroup, TextArea } from '@wodi/ui';
import { useState } from 'react';

import { closeLabour, labourInError, maternityKeys } from '../api/maternity.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import { errorText, InErrorDialog } from './common.tsx';
import styles from './maternity.module.css';

// Ending a labour record without a delivery here (plan §2.5, M22): closed with an outcome
// (false labour, transferred out, other), or entered in error with a reason. Closing leaves
// the pregnancy open; a new labour record can be opened for it later.

/** "Closed: false labour." or the reason it was entered in error, for the description. */
export function labourEndWords(labour: LabourRecordView['labour']): string {
  if (labour.status === 'entered_in_error' && labour.statusReason !== null) {
    return ` Entered in error: ${labour.statusReason}`;
  }
  if (labour.status !== 'closed' || labour.closeOutcome === null) return '';
  const note = labour.closeNote === null ? '' : ` ${labour.closeNote}`;
  return ` Closed: ${LABOUR_CLOSE_OUTCOME_LABELS[labour.closeOutcome].toLowerCase()}.${note}`;
}

function useSetView(view: LabourRecordView) {
  const queryClient = useQueryClient();
  return (next: LabourRecordView) => {
    queryClient.setQueryData(maternityKeys.labour(view.labour.id), next);
    void queryClient.invalidateQueries({ queryKey: maternityKeys.labourWard });
    void queryClient.invalidateQueries({ queryKey: maternityKeys.patient(next.patientId) });
  };
}

function CloseLabourDialog({ view, onClose }: { view: LabourRecordView; onClose: () => void }) {
  const setView = useSetView(view);
  const [outcome, setOutcome] = useState<LabourCloseOutcome>('false_labour');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () =>
      closeLabour(view.labour.id, { outcome, note: note.trim() === '' ? null : note.trim() }),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Labour closed' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Close labour"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        For labour that ends without a delivery here. The pregnancy stays open; recording a delivery
        closes labour by itself.
      </p>
      <RadioGroup
        label="Outcome"
        value={outcome}
        onChange={(v) => {
          setOutcome(v as LabourCloseOutcome);
        }}
        options={LABOUR_CLOSE_OUTCOMES.map((o) => ({
          id: o,
          label: LABOUR_CLOSE_OUTCOME_LABELS[o],
        }))}
      />
      <TextArea label="Note" value={note} onChange={setNote} rows={2} />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Nothing was changed. Try again.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Close labour
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The labour record's own actions: close it, or enter it in error. */
export function LabourActions({ view }: { view: LabourRecordView }) {
  const online = useOnline();
  const setView = useSetView(view);
  const [dialog, setDialog] = useState<'close' | 'error' | null>(null);
  const mark = useMutation({
    mutationFn: (reason: string) => labourInError(view.labour.id, reason),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Labour record entered in error' });
      setDialog(null);
    },
  });
  const status = view.labour.status;
  const delivered = view.delivery?.status === 'active';
  return (
    <>
      {status === 'in_labour' ? (
        <Button
          variant="secondary"
          dense
          isDisabled={!online}
          onPress={() => {
            setDialog('close');
          }}
        >
          Close labour
        </Button>
      ) : null}
      {status === 'entered_in_error' || delivered ? null : (
        <Button
          variant="quiet"
          dense
          isDisabled={!online}
          onPress={() => {
            setDialog('error');
          }}
        >
          Enter in error
        </Button>
      )}
      {dialog === 'close' ? (
        <CloseLabourDialog
          view={view}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'error' ? (
        <InErrorDialog
          what="this labour record"
          onClose={() => {
            setDialog(null);
          }}
          onConfirm={(reason) => {
            mark.mutate(reason);
          }}
          pending={mark.isPending}
          error={mark.error}
        />
      ) : null}
    </>
  );
}
